import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Link
} from '@mui/material';
import { Close } from '@mui/icons-material';

interface DisclaimerModalProps {
    open: boolean;
    onClose: () => void;
}

const DisclaimerModal: React.FC<DisclaimerModalProps> = ({ open, onClose }) => {
    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 3, pr: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                        Important Disclaimer
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ p: 3, pt: 0 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" paragraph sx={{ lineHeight: 1.6 }}>
                        The data provided is part of our beta version and is intended to support your research process. 
                        Please note that this information has not yet undergone full verification or review. We strongly 
                        encourage all researchers to independently verify the data before drawing conclusions or sharing externally.
                    </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" paragraph sx={{ lineHeight: 1.6 }}>
                        If you uncover credible evidence of complicity or problematic activity by any entity or individual 
                        in the data, we welcome and appreciate you sharing it with us. This will help us strengthen the 
                        accuracy and integrity of the platform. We will be creating a process for evidence intake. For the 
                        time being please email any evidence found to{' '}
                        <Link 
                            href="mailto:info@ethics.vc" 
                            sx={{ 
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            info@ethics.vc
                        </Link>.
                    </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                        We'll notify all users once our internal verification processes are fully in place. Thank you for your partnership.
                    </Typography>
                </Box>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" sx={{ color: 'error.light', fontWeight: 500 }}>
                        <strong>Beta Version Notice:</strong> This platform is currently in beta. Data accuracy and completeness may vary.
                    </Typography>
                </Box>
            </DialogContent>
            
            <DialogActions sx={{ p: 3, pt: 0 }}>
                <Button 
                    onClick={onClose} 
                    variant="contained" 
                    sx={{ minWidth: 100 }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DisclaimerModal;